/**
 * Helper functions for working with category hierarchies
 */

import type { TransformedWPCategory } from "../types/productTaxonomyApiTypes";

/**
 * Extended category interface with children for hierarchical structure
 */
export interface CategoryWithChildren extends TransformedWPCategory {
  children: CategoryWithChildren[];
}

/**
 * Create a map of categories by ID for easy lookup
 *
 * @param categories - Array of category objects
 * @returns Map of categories with ID as key
 */
export const createCategoryMap = (
  categories: TransformedWPCategory[] | undefined
): Map<number, TransformedWPCategory> => {
  if (!categories || !categories.length) return new Map();

  const map = new Map<number, TransformedWPCategory>();
  categories.forEach((cat) => {
    map.set(cat.id, cat);
  });

  return map;
};

/**
 * Build a hierarchical tree structure of categories
 *
 * @param categories - Array of category objects
 * @returns Array of root categories with nested children
 */
export const buildCategoryTree = (
  categories: TransformedWPCategory[] | undefined
): CategoryWithChildren[] => {
  if (!categories || !categories.length) return [];

  // Create a map for quick lookup
  const map = new Map<number, CategoryWithChildren>();
  categories.forEach((cat) => {
    map.set(cat.id, {
      ...cat,
      children: [],
    });
  });

  // Build the tree structure
  const rootCategories: CategoryWithChildren[] = [];
  categories.forEach((cat) => {
    const categoryWithChildren = map.get(cat.id);

    if (!categoryWithChildren) return;

    if (cat.parent === 0) {
      // This is a root category
      rootCategories.push(categoryWithChildren);
    } else {
      // This is a child category
      const parent = map.get(cat.parent);
      if (parent) {
        parent.children.push(categoryWithChildren);
      } else {
        // If parent doesn't exist, treat as root
        rootCategories.push(categoryWithChildren);
      }
    }
  });

  return rootCategories;
};

/**
 * Find all parent categories for a given category
 *
 * @param categoryId - ID of the category to find parents for
 * @param categoryMap - Map of categories by ID
 * @returns Array of parent category IDs
 */
export const getParentCategories = (
  categoryId: number,
  categoryMap: Map<number, TransformedWPCategory>
): number[] => {
  const parents: number[] = [];
  let current = categoryMap.get(categoryId);

  while (current && current.parent !== 0) {
    const parent = categoryMap.get(current.parent);
    if (parent) {
      parents.push(parent.id);
      current = parent;
    } else {
      break;
    }
  }

  return parents;
};

/**
 * Find all subcategories (direct and indirect) for a given category
 *
 * @param categoryId - ID of the category to find subcategories for
 * @param categoryMap - Map of categories by ID
 * @returns Array of subcategory IDs
 */
export const getSubcategories = (
  categoryId: number,
  categoryMap: Map<number, TransformedWPCategory>
): number[] => {
  const subcategories: number[] = [];
  const category = categoryMap.get(categoryId);

  if (!category) return subcategories;

  // Find direct children
  const directChildren = Array.from(categoryMap.values())
    .filter((cat) => cat.parent === category.id)
    .map((cat) => cat.id);

  // Add direct children
  subcategories.push(...directChildren);

  // Recursively add their children
  directChildren.forEach((childId) => {
    subcategories.push(...getSubcategories(childId, categoryMap));
  });

  return subcategories;
};

/**
 * Check if a category has products (directly or through children)
 *
 * @param category - Category object with count and optional children
 * @returns True if category has products, false otherwise
 */
export const hasCategoryProducts = (
  category: CategoryWithChildren
): boolean => {
  // Check if this category has products
  if (category.count > 0) {
    return true;
  }

  // If this category has children, check if any children have products
  if (category.children && category.children.length > 0) {
    return category.children.some((child) => hasCategoryProducts(child));
  }

  return false;
};
